/*
 * @Author: zhushitao
 * @Date: 2021-07-14 19:57:57
 * @LastEditors: zhushitao
 * @LastEditTime: 2021-11-03 17:50:29
 * @Description:
 */
import { inject, observer } from 'mobx-react';
import React, { lazy, Suspense, useContext, useMemo } from 'react';
import { AppContextType } from '@byted-cg/linkchat-plugin';
import ErrorBoundary from '../components/ErrorBoundary';
import { LocalProvider } from './index';
import { IComponent } from './type';

const defaultComponentConfig = {
  code: 'default',
};

export interface ILazyComponent {
  config: IComponent;
  [propName: string]: any;
  fallback?: boolean;
  appContext?: AppContextType;
}

const LazyComponent: React.FC<ILazyComponent> = ({
  config,
  appContext,
  fallback = true,
  ref,
  ...props
}) => {
  try {
    if (!config.isRemote) {
      //If there is a cache, the Promise component will not be removed.
      const libNameArr = [config.code];
      if (config.version) {
        libNameArr.push(config.version);
      }
      const libName = libNameArr.join('_');
      //Here, it is preferred to go to the cache to get the component, because it may be registered in advance.
      const CacheComponent =
        appContext?.libraray.libMap.get(libName) || config.component;
      const C = inject<{
        appContext: AppContextType;
      }>(({ appContext }) => ({
        appContext,
      }))(observer(CacheComponent));
      return React.createElement(C, {
        ...props,
        defaultConfig: config,
        ref,
      });
    }
  } catch (error) {
    return null;
  }
  const { getRemoteComponent } = useContext(LocalProvider);
  const CurrentComponent: any = useMemo(() => {
    try {
      return lazy(() => getRemoteComponent(config || defaultComponentConfig));
    } catch (error) {
      appContext?.logger?.error(`LazyComponentError_${config.code}`, {
        error: error,
      });
    }
  }, [JSON.stringify(config)]);

  const LazyLoading = () => {
    if (config.meta?.componentType !== 'card') return null;
    let isAutoCard = config?.meta?.avatar === 'none';
    return (
      <div className={`lazyLoading ${isAutoCard && 'lazyLoadingMid'}`}>
        <div className="lazyLoading_line lazyLoading_line_short"></div>
        <div className="lazyLoading_line"></div>
        <div className="lazyLoading_line"></div>
      </div>
    );
  };

  return (
    <ErrorBoundary errorRender={<React.Fragment />}>
      <Suspense fallback={props.loadIcon || <LazyLoading />}>
        <CurrentComponent
          appContext={appContext}
          {...props}
          ref={ref}
          defaultConfig={config}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(LazyComponent));
